import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import styles from "./BundleBuilderBundle.module.css";
import { SubStepType } from "../../Types";
import { BundleBuilderBundleContext, ExtendedStepsType } from "./BundleBuilderBundle";
import AngleLeftIcon from "../../../../components/Icons/AngleLeftIcon";
import ClickAwayListener from "../../BundlesComponents/ClickAwayListener/ClickAwayListener";
import AngleRightIcon from "../../../../components/Icons/AngleRightIcon";
import { CheckIcon } from "@shopify/polaris-icons";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { hexToHsla, lightenHexColor } from "../../../../utilities/parsers";

const SCROLL_STEP = 120; // px to scroll on button click

export const RenderSubStepBadges = () => {
    const { activeSubStep, setActiveSubStep, steps, activeStep, bundleConfig, isMobile } =
        useContext(BundleBuilderBundleContext);
    const mobileLayout = bundleConfig?.bundles_design?.mobile_substep_header_layout || "dropdown";

    const subStepsContainerRef = useRef<HTMLDivElement>(null);

    return (
        <div
            className={`${isMobile && mobileLayout === "scrollable_tabs" ? styles.subStepsBadgesScrollable : styles.subStepsBadges}`}
            ref={subStepsContainerRef}
        >
            {steps[activeStep]?.subSteps.map((subStep: SubStepType, idx: number) => {
                return (
                    <div
                        className={`${styles.subStepBadge} ${idx == activeSubStep && styles.active}`}
                        onClick={() => setActiveSubStep(idx)}
                        key={subStep?.step_label + idx}
                        style={{
                            // @ts-expect-error required
                            "--substep-bg-color": bundleConfig?.bundles_design?.substep_bg_color,
                            "--substep-text-color": bundleConfig?.bundles_design?.substep_text_color,
                            fontSize: bundleConfig?.bundles_design?.substep_header_text_size
                                ? bundleConfig?.bundles_design?.substep_header_text_size + "px"
                                : "11px",
                        }}
                    >
                        {subStep.step_label || "-"}
                    </div>
                );
            })}
        </div>
    );
};

export const RenderSubStepDropdown = ({
    subStepDropdownOpen,
    setSubStepDropdownOpen,
}: {
    subStepDropdownOpen: boolean;
    setSubStepDropdownOpen: Dispatch<SetStateAction<boolean>>;
}) => {
    const { steps, activeStep, activeSubStep, setActiveSubStep, bundleConfig } = useContext(BundleBuilderBundleContext);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        setShowDropdown(steps[activeStep]?.subSteps?.length > 1 ? true : false);
    }, [steps[activeStep]?.subSteps]);

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                minWidth: 200,
                maxWidth: 200,
                margin: "auto",
                marginBottom: 20,
            }}
        >
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="substepDropdown">
                <div
                    className={`${styles.dropdownOptSelected}`}
                    style={{
                        width: "100%",
                        // @ts-expect-error required
                        "--substep-bg-color": bundleConfig?.bundles_design?.substep_bg_color,
                        "--substep-text-color": bundleConfig?.bundles_design?.substep_text_color,
                        cursor: showDropdown ? "pointer" : "default",
                        fontSize: bundleConfig?.bundles_design?.substep_header_text_size
                            ? bundleConfig?.bundles_design?.substep_header_text_size + "px"
                            : "14px",
                        fontWeight: "750",
                    }}
                    onClick={(e) => {
                        e?.stopPropagation();
                        e.preventDefault();
                        if (showDropdown) setSubStepDropdownOpen((ps) => !ps);
                    }}
                >
                    <span
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                        }}
                    >
                        {steps[activeStep]?.subSteps[activeSubStep]?.step_label || "SubStep " + (+activeSubStep + 1)}
                    </span>
                    {showDropdown && (
                        <span
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                transform: subStepDropdownOpen ? "rotate(90deg)" : "rotate(270deg)",
                                flexShrink: 0,
                                marginLeft: "8px",
                            }}
                        >
                            <AngleLeftIcon
                                color={"white"}
                                size={
                                    bundleConfig?.bundles_design?.substep_header_text_size
                                        ? bundleConfig?.bundles_design?.substep_header_text_size + "px"
                                        : "0.7rem"
                                }
                            />
                        </span>
                    )}
                </div>
            </InspectorEditableBlock>
            {subStepDropdownOpen && (
                <ClickAwayListener onClickAway={() => setSubStepDropdownOpen(false)}>
                    <div className={styles.dropdownOptionsWrapper}>
                        {steps[activeStep]?.subSteps?.map((subStep: SubStepType, idx: number) => {
                            return (
                                <div
                                    className={`${styles.dropdownOpt} ${idx == activeSubStep && styles.activeOpt}`}
                                    key={idx + subStep?.step_label}
                                    onClick={(e) => {
                                        e?.stopPropagation();
                                        e.preventDefault();
                                        setActiveSubStep(idx);
                                        setSubStepDropdownOpen(false);
                                    }}
                                    style={{
                                        // @ts-expect-error required
                                        "--substep-bg-color": bundleConfig?.bundles_design?.substep_bg_color,
                                        "--substep-text-color": bundleConfig?.bundles_design?.substep_text_color,
                                        fontSize: bundleConfig?.bundles_design?.substep_header_text_size
                                            ? bundleConfig?.bundles_design?.substep_header_text_size + "px"
                                            : "14px",
                                        fontWeight: "750",
                                    }}
                                >
                                    {subStep?.step_label || "SubStep " + (+idx + 1)}
                                </div>
                            );
                        })}
                    </div>
                </ClickAwayListener>
            )}
        </div>
    );
};

const StepsHeaderBar = () => {
    const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const { steps = [], activeStep, isMobile, bundleConfig, standAlone } = useContext(BundleBuilderBundleContext);

    useEffect(() => {
        if (stepRefs.current[activeStep] && scrollContainerRef.current && standAlone) {
            const activeElement = stepRefs.current[activeStep];
            const container = scrollContainerRef.current;

            const activeLeft = activeElement.offsetLeft;
            const activeWidth = activeElement.offsetWidth;
            const containerWidth = container.offsetWidth;

            container.scrollTo({
                left: activeLeft - containerWidth / 2 + activeWidth / 2,
                behavior: "smooth",
            });
        }
    }, [activeStep]);

    useEffect(() => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            setIsOverflowing(container.scrollWidth > container.clientWidth);
        }
    }, [steps, isMobile, scrollContainerRef]);

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: -SCROLL_STEP,
                behavior: "smooth",
            });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: SCROLL_STEP,
                behavior: "smooth",
            });
        }
    };

    return (
        <>
            {steps.length > 1 ? (
                <div
                    style={{
                        borderBottom: "2px solid rgba(227, 230, 227, 1)",
                        margin: "auto",
                        marginBottom: 20,
                        paddingBottom: isMobile ? "10px" : "20px",
                        position: "relative",
                        overflow: "hidden",
                        maxWidth: isMobile ? "90%" : "90%",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <span
                        className={`${styles.progressSlip}`}
                        style={{
                            width: "100%",
                            background: bundleConfig?.bundles_design?.step_circle_color || "#3F3F3F",
                            top: isMobile ? "20%" : "28%",
                        }}
                    ></span>
                    {isOverflowing && (
                        <span
                            onClick={scrollLeft}
                            className={`${styles.arrowsForSlide} ${styles.leftSlideArrow}`}
                            style={{
                                boxShadow: `${bundleConfig?.bundles_design?.bundle_widget_bg} 3px 0 20px 10px`,
                                marginTop: isMobile ? 7 : 18,
                                backgroundColor: bundleConfig?.bundles_design?.step_circle_color,
                            }}
                        >
                            <AngleLeftIcon size={12} color={"white"} />
                        </span>
                    )}
                    <div
                        ref={scrollContainerRef}
                        className={`${styles.bbStepsWrapper}`}
                        style={{
                            width: "100%",
                            // maxWidth: `min-content`,
                            display: "flex",
                            gap: 20,
                            overflowX: "auto",
                            scrollBehavior: "smooth",
                            minHeight: isMobile ? 90 : 90,
                            overflowY: "hidden",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none", // IE & Edge
                            paddingInline: 10,
                        }}
                    >
                        {steps.map((step: ExtendedStepsType, index: number) => (
                            <div
                                key={index}
                                ref={(el) => (stepRefs.current[index] = el)}
                                className={`${styles.stepWrapper}
                         ${activeStep >= index ? styles.activeStep : styles.inactiveStep} `}
                                style={{
                                    minWidth: isMobile ? 80 : 110,
                                    flex: "0 0 auto",
                                    textAlign: "center",
                                    marginBottom: isMobile ? 55 : 30,
                                    "--circleBorderColor": bundleConfig?.bundles_design?.step_circle_color,
                                    "--lightCircleBorderColor": lightenHexColor(
                                        bundleConfig?.bundles_design?.step_circle_color,
                                        70,
                                    ),
                                }}
                            >
                                <div
                                    className={styles.stepIconWrapper}
                                    style={{
                                        backgroundColor:
                                            activeStep > index
                                                ? bundleConfig?.bundles_design?.step_circle_bg_color
                                                : "#ffffff",
                                    }}
                                >
                                    {activeStep > index ? (
                                        <CheckIcon
                                            style={{
                                                fill:
                                                    hexToHsla(bundleConfig?.bundles_design?.step_circle_bg_color)
                                                        .brightness >= 0.5
                                                        ? "#000000"
                                                        : "#ffffff",
                                                stroke: "none",
                                                width: "auto",
                                                height: "auto",
                                            }}
                                        />
                                    ) : (
                                        <span className={styles.stepIconImg}>
                                            {step?.step_icon?.originalSrc ? (
                                                <img
                                                    src={step?.step_icon?.originalSrc}
                                                    alt={step?.step_icon?.alt}
                                                    style={{
                                                        borderRadius: 0,
                                                        width: "100%",
                                                        objectFit: "contain",
                                                        objectPosition: "center",
                                                    }}
                                                />
                                            ) : (
                                                index + 1
                                            )}
                                        </span>
                                    )}
                                </div>
                                <span className={styles.stepMeta}>
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="stepLabel"
                                    >
                                        <span
                                            className={styles.stepTitle}
                                            style={{
                                                width: isMobile ? "100%" : "max-content",
                                                textAlign: "center",
                                                color: bundleConfig?.bundles_design?.step_label_color,
                                            }}
                                        >
                                            {step?.step_label}
                                        </span>
                                    </InspectorEditableBlock>
                                </span>
                                {/* {steps?.length - 1 != index && (
                            <span
                                className={`${styles.progressSlip} ${activeStep > index && styles.activeSlip}`}
                            ></span>
                        )} */}
                            </div>
                        ))}
                    </div>
                    {isOverflowing && (
                        <span
                            onClick={scrollRight}
                            className={`${styles.arrowsForSlide} ${styles.rightSlideArrow}`}
                            style={{
                                boxShadow: `${bundleConfig?.bundles_design?.bundle_widget_bg} -3px 0 20px 10px`,
                                marginTop: isMobile ? 7 : 18,
                                backgroundColor: bundleConfig?.bundles_design?.step_circle_color,
                            }}
                        >
                            <AngleRightIcon size={12} color={"white"} />
                        </span>
                    )}
                </div>
            ) : (
                <></>
            )}
        </>
    );
};

export default StepsHeaderBar;
